import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useCallback, useEffect, useMemo, useState } from 'react';

import defaultClient from '@src/api/clients/default';
import ClientDropdown from '@src/components/ClientDropdown';
import PushConfirmDialog from '@src/pages/app/facebook/components/PushConfirmDialog';
import { fetchClientVerticalOptions } from '@src/api/actions/verticals';
import {
  previewFacebookZipTargeting,
  pushFacebookZipTargeting,
  fetchFacebookAdSetTargeting,
  fetchFacebookZipPushLog,
  fetchFacebookZipPushHistory,
  fetchFacebookZipPreviewMap,
  setFacebookObjectStatus,
} from '@src/api/actions';
import SelectionCoverageMap from '@src/pages/app/facebook/components/SelectionCoverageMap';
import { useAuth, useMessage } from '@src/context/common';

function parseCampaignVertical(campaignName) {
  if (!campaignName) {
    return null;
  }
  try {
    const normalized = campaignName.startsWith('?') ? campaignName : `?${campaignName}`;
    const params = new URLSearchParams(normalized);
    const vertical = params.get('v') || params.get('vertical');
    return vertical ? vertical.trim() : null;
  } catch (error) {
    return null;
  }
}

const PREVIEW_SAMPLE_SIZE = 20;
const USD_CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const USD_WHOLE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// A territory is a viable Campaign Builder target only when it has active ZIPs
// that aren't ALL paused out of Facebook targeting. Paused ZIPs stay active
// (so historical spend stays attributed — see ClientZipVertical.targeting_paused)
// but push zero geo, so a fully-paused territory (is_paused) would show budget
// here that never reaches Facebook. Partially-paused territories still target
// their non-paused ZIPs, so they remain viable.
const territoryIsTargetable = (territory) =>
  (territory?.active_zip_count || 0) > 0 && !territory?.is_paused;

function CampaignBuilderPage() {
  const message = useMessage();
  const { state: authState } = useAuth();
  const isStaffUser = Boolean(
    authState?.user?.is_staff || authState?.user?.is_superuser
  );

  // {objectType, objectId, objectName, nextStatus} — pending pause/resume
  // awaiting confirmation in the status dialog.
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);

  const [clientsError, setClientsError] = useState('');

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [structure, setStructure] = useState([]);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structureRefreshing, setStructureRefreshing] = useState(false);
  const [structureError, setStructureError] = useState('');

  const [territories, setTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(false);
  const [territoriesError, setTerritoriesError] = useState('');
  const [budgetTerritoriesLoading, setBudgetTerritoriesLoading] = useState(false);
  const [didAutoApplyBudgetTerritories, setDidAutoApplyBudgetTerritories] = useState(false);
  const [budgetSegments, setBudgetSegments] = useState([]);
  const [budgetSegmentsLoaded, setBudgetSegmentsLoaded] = useState(false);
  const [budgetMonthLabel, setBudgetMonthLabel] = useState('');

  const [verticals, setVerticals] = useState([]);
  const [verticalsLoading, setVerticalsLoading] = useState(true);
  const [verticalsError, setVerticalsError] = useState('');

  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedAdSetIds, setSelectedAdSetIds] = useState([]);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false);

  const [selectedVerticalIds, setSelectedVerticalIds] = useState([]);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState([]);
  const [includeFilteredOut, setIncludeFilteredOut] = useState(false);

  const [preview, setPreview] = useState({ zip_count: 0, sample_zip_codes: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [adSetTargeting, setAdSetTargeting] = useState(null);
  const [adSetTargetingLoading, setAdSetTargetingLoading] = useState(false);
  const [adSetTargetingError, setAdSetTargetingError] = useState('');
  const [pushLog, setPushLog] = useState([]);
  const [pushHistory, setPushHistory] = useState([]);
  const [pushHistoryLoading, setPushHistoryLoading] = useState(false);
  const [pushHistoryError, setPushHistoryError] = useState('');

  const [selectionMapItems, setSelectionMapItems] = useState([]);
  const [removedMapItems, setRemovedMapItems] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapTruncated, setMapTruncated] = useState(false);

  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState('');
  const [lastPush, setLastPush] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [matchVerticalsToCampaigns, setMatchVerticalsToCampaigns] = useState(false);

  const buildFacebookAdsManagerUrl = (accountId, adSetIds) => {
    const base = `https://adsmanager.facebook.com/adsmanager/manage/adsets?act=${accountId}`;
    if (adSetIds?.length) {
      return `${base}&selected_adset_ids=${adSetIds.join(',')}`;
    }
    return base;
  };

  useEffect(() => {
    if (!selectedClientId) {
      setVerticals([]);
      setSelectedVerticalIds([]);
      return;
    }

    setVerticalsLoading(true);
    setVerticalsError('');
    fetchClientVerticalOptions({ clientId: selectedClientId })
      .then((response) => {
        const fetched = response.data || [];
        setVerticals(fetched);
        setSelectedVerticalIds(fetched.map((vertical) => vertical.id));
      })
      .catch((error) => {
        setVerticalsError(error?.message || 'Failed to load verticals.');
      })
      .finally(() => setVerticalsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setStructure([]);
      setSelectedAdAccountId('');
      setSelectedCampaignIds([]);
      setSelectedAdSetIds([]);
      return;
    }
    let cancelled = false;
    let liveArrived = false;
    setStructureLoading(true);
    setStructureRefreshing(true);
    setStructureError('');

    // Fast paint from the DB copy — no Facebook API round-trip. The live
    // fetch below is authoritative and replaces it when it lands (statuses,
    // merged ad sets, 30-day spend), so a DB miss here is non-fatal.
    defaultClient
      .request({
        url: `dashboard/facebook/ad-structure-db/?client_id=${selectedClientId}`,
        method: 'GET',
      })
      .then((response) => {
        if (!cancelled && !liveArrived) {
          setStructure(response.data || []);
          setStructureLoading(false);
        }
      })
      .catch(() => {});

    defaultClient
      .request({
        url: `dashboard/facebook/ad-structure/?client_id=${selectedClientId}&include_inactive=${showInactiveCampaigns}`,
        method: 'GET',
      })
      .then((response) => {
        liveArrived = true;
        if (!cancelled) {
          setStructure(response.data || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setStructureError(error?.message || 'Failed to load Facebook structure.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStructureLoading(false);
          setStructureRefreshing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClientId, showInactiveCampaigns]);

  useEffect(() => {
    if (!selectedClientId) {
      setTerritories([]);
      return;
    }
    setTerritoriesLoading(true);
    setTerritoriesError('');
    setSelectedTerritoryIds([]);
    defaultClient
      .request({
        url: `dashboard/client-territories/?client_id=${selectedClientId}`,
        method: 'GET',
      })
      .then((response) => {
        setTerritories(response.data);
      })
      .catch((error) => {
        setTerritoriesError(error?.message || 'Failed to load territories.');
      })
      .finally(() => setTerritoriesLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!territories.length) {
      setSelectedTerritoryIds([]);
      return;
    }
    const targetable = territories.filter(territoryIsTargetable);
    const withActiveZips = territories.filter(
      (territory) => (territory.active_zip_count || 0) > 0
    );
    setSelectedTerritoryIds(
      targetable.length
        ? targetable.map((territory) => territory.id)
        // Territories exist but every one is fully paused → select none rather
        // than auto-selecting markets that push zero geo.
        : withActiveZips.length
          ? []
          // No active ZIPs anywhere (e.g. counts not yet loaded) → legacy
          // fallback so the selector isn't left empty.
          : territories.map((territory) => territory.id)
    );
  }, [territories]);

  useEffect(() => {
    setDidAutoApplyBudgetTerritories(false);
    setBudgetSegments([]);
    setBudgetSegmentsLoaded(false);
    setBudgetMonthLabel('');

    if (!selectedClientId) {
      return undefined;
    }

    let cancelled = false;
    setBudgetTerritoriesLoading(true);
    const today = new Date();
    setBudgetMonthLabel(today.toLocaleString('default', { month: 'long', year: 'numeric' }));
    defaultClient
      .request({
        url: 'dashboard/client-budgets/pacing-segments/',
        method: 'GET',
        payload: {
          client_id: selectedClientId,
          month: today.getMonth() + 1,
          year: today.getFullYear(),
        },
      })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const rows = Array.isArray(response.data)
          ? response.data
          : response?.data?.results || [];
        setBudgetSegments(rows);
      })
      .catch((error) => {
        if (!cancelled) {
          message.error(error?.message || 'Failed to load budget segments.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBudgetTerritoriesLoading(false);
          setBudgetSegmentsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // A budget segment applies to the current selection when its vertical scope
  // intersects the selected verticals — or when it has no vertical scope at
  // all. Several clients fund every vertical with one unscoped segment; the
  // old per-vertical fetch dropped those entirely.
  const { territoryBudgetSummary, clientWideBudget } = useMemo(() => {
    const summaryByTerritory = {};
    let clientWide = null;
    const selectedVerticalIdSet = new Set(
      selectedVerticalIds.map((id) => Number(id))
    );

    budgetSegments.forEach((row) => {
      const totalAmount = Number(row?.budget_amount);
      if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
        return;
      }
      const rowVerticalIds = [
        ...(Array.isArray(row?.vertical_ids) ? row.vertical_ids : []),
        ...(row?.vertical_id ? [row.vertical_id] : []),
      ]
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0);
      const appliesToSelection =
        !rowVerticalIds.length ||
        rowVerticalIds.some((id) => selectedVerticalIdSet.has(id));
      if (!appliesToSelection) {
        return;
      }

      const remaining = Number(row?.budget_remaining || 0);
      const segmentTerritoryIds = new Set();
      if (row?.location_id) {
        segmentTerritoryIds.add(Number(row.location_id));
      }
      if (Array.isArray(row?.location_ids)) {
        row.location_ids.forEach((locationId) =>
          segmentTerritoryIds.add(Number(locationId))
        );
      }
      const validTerritoryIds = [...segmentTerritoryIds].filter(
        (territoryId) => Number.isFinite(territoryId) && territoryId > 0
      );

      if (!validTerritoryIds.length) {
        // No location scope — a client-wide budget covering every territory.
        clientWide = {
          total: (clientWide?.total || 0) + totalAmount,
          remaining:
            (clientWide?.remaining || 0) +
            (Number.isFinite(remaining) ? remaining : 0),
        };
        return;
      }

      validTerritoryIds.forEach((territoryId) => {
        const prior = summaryByTerritory[territoryId] || { total: 0, remaining: 0 };
        summaryByTerritory[territoryId] = {
          total: prior.total + totalAmount,
          remaining:
            prior.remaining + (Number.isFinite(remaining) ? remaining : 0),
        };
      });
    });

    return { territoryBudgetSummary: summaryByTerritory, clientWideBudget: clientWide };
  }, [budgetSegments, selectedVerticalIds]);

  const clientWideFunded = Boolean(clientWideBudget && clientWideBudget.remaining > 0);

  const applyFundedTerritories = useCallback(({ auto = false } = {}) => {
    if (!territories.length) {
      return;
    }
    const fundedIds = territories
      .filter((territory) => {
        // A fully-paused market pushes zero ZIPs, so never auto-select it even
        // when it still carries a funded budget segment.
        if (!territoryIsTargetable(territory)) {
          return false;
        }
        const summary = territoryBudgetSummary[Number(territory.id)];
        if (summary) {
          return summary.remaining > 0;
        }
        // Territories without their own segment ride on a funded client-wide
        // budget.
        return clientWideFunded;
      })
      .map((territory) => Number(territory.id));
    const budgetedCount = Object.keys(territoryBudgetSummary).length;
    const exhaustedCount = Object.values(territoryBudgetSummary).filter(
      (summary) => summary.remaining <= 0
    ).length;
    const monthName = new Date().toLocaleString('default', { month: 'long' });

    if (fundedIds.length) {
      setSelectedTerritoryIds(fundedIds);
      if (!auto) {
        const exhaustedSuffix = exhaustedCount > 0
          ? ` (skipped ${exhaustedCount} with no budget left)`
          : '';
        message.success(
          `Loaded ${fundedIds.length} funded territor${fundedIds.length === 1 ? 'y' : 'ies'} for ${monthName}${exhaustedSuffix}.`
        );
      }
    } else if (!auto) {
      // Never clear the selection here — empty means "push nothing", and
      // budget data being absent is not proof the territories are unfunded.
      message.info(
        budgetedCount
          ? `All ${budgetedCount} budgeted territor${budgetedCount === 1 ? 'y has' : 'ies have'} no budget left for ${monthName}. Selection left unchanged.`
          : `No territory budgets found for ${monthName}. Selection left unchanged.`
      );
    }
    if (auto) {
      setDidAutoApplyBudgetTerritories(true);
    }
  }, [territories, territoryBudgetSummary, clientWideFunded, message]);

  useEffect(() => {
    if (
      !selectedClientId ||
      !territories.length ||
      !budgetSegmentsLoaded ||
      verticalsLoading ||
      didAutoApplyBudgetTerritories
    ) {
      return;
    }
    applyFundedTerritories({ auto: true });
  }, [
    selectedClientId,
    territories,
    budgetSegmentsLoaded,
    verticalsLoading,
    didAutoApplyBudgetTerritories,
    applyFundedTerritories,
  ]);

  // NOTE: exhausted ("no budget left") territories are intentionally kept in
  // the selection so they can be manually overridden in the builder.

  useEffect(() => {
    if (!structure.length) {
      setSelectedAdAccountId('');
      return;
    }
    const defaultAccount = structure[0];
    setSelectedAdAccountId((prev) => prev || defaultAccount.account_id);
  }, [structure]);

  const selectedAccount = useMemo(
    () => structure.find((account) => account.account_id === selectedAdAccountId) || null,
    [structure, selectedAdAccountId]
  );

  useEffect(() => {
    const campaigns = selectedAccount?.campaigns || [];
    if (!campaigns.length) {
      setSelectedCampaignIds([]);
      return;
    }
    setSelectedCampaignIds((prev) => {
      const validPrev = prev.filter((id) =>
        campaigns.some((campaign) => campaign.campaign_id === id)
      );
      if (validPrev.length) {
        return validPrev;
      }
      const firstWithAdSets = campaigns.find(
        (campaign) => (campaign?.ad_sets || []).length > 0
      );
      return [firstWithAdSets?.campaign_id || campaigns[0].campaign_id];
    });
  }, [selectedAccount]);

  const selectedCampaigns = useMemo(
    () =>
      (selectedAccount?.campaigns || []).filter((campaign) =>
        selectedCampaignIds.includes(campaign.campaign_id)
      ),
    [selectedAccount, selectedCampaignIds]
  );

  const adSetOptions = useMemo(() => {
    const seen = {};
    selectedCampaigns.forEach((campaign) => {
      (campaign.ad_sets || []).forEach((adSet) => {
        if (adSet?.ad_set_id && !seen[adSet.ad_set_id]) {
          seen[adSet.ad_set_id] = adSet;
        }
      });
    });
    return Object.values(seen);
  }, [selectedCampaigns]);

  const verticalIdByName = useMemo(() => {
    const map = {};
    verticals.forEach((vertical) => {
      if (vertical.vertical) {
        map[vertical.vertical.toLowerCase()] = vertical.id;
      }
    });
    return map;
  }, [verticals]);

  // Campaign → vertical, preferring the backend's alias-aware resolution
  // (campaign FK or v= param through VerticalAlias — "Roofs" → Roofing).
  // Raw-name parsing is only the fallback for stale cached structures.
  const campaignVerticalInfo = useCallback(
    (campaign) => {
      if (campaign?.vertical_id) {
        return { id: campaign.vertical_id, name: campaign.vertical || null };
      }
      const parsed = parseCampaignVertical(campaign?.campaign_name);
      const id = parsed ? verticalIdByName[parsed.toLowerCase()] : null;
      return { id: id || null, name: parsed || null };
    },
    [verticalIdByName]
  );

  useEffect(() => {
    if (!selectedCampaigns.length || !verticals.length) {
      return;
    }
    const resolvedIds = new Set();
    const targetVertNames = new Set();
    selectedCampaigns.forEach((campaign) => {
      const info = campaignVerticalInfo(campaign);
      if (info.id) {
        resolvedIds.add(info.id);
      } else if (info.name) {
        targetVertNames.add(info.name.toLowerCase());
      }
    });
    if (!resolvedIds.size && !targetVertNames.size) {
      return;
    }
    const matched = verticals.filter(
      (vertical) =>
        resolvedIds.has(vertical.id) ||
        targetVertNames.has((vertical.vertical || '').toLowerCase())
    );
    if (!matched.length) {
      return;
    }
    setSelectedVerticalIds(matched.map((vertical) => vertical.id));
  }, [selectedCampaigns, verticals, campaignVerticalInfo]);

  useEffect(() => {
    if (!adSetOptions.length) {
      setSelectedAdSetIds([]);
      return;
    }
    setSelectedAdSetIds((prev) => {
      const filtered = prev.filter((id) =>
        adSetOptions.some((adSet) => adSet.ad_set_id === id)
      );
      if (filtered.length) {
        return filtered;
      }
      // Default to every active ad set — pushes target whole campaigns, and
      // pre-selecting a single ad set silently missed the rest.
      const activeIds = adSetOptions
        .filter((adSet) => adSet.active)
        .map((adSet) => adSet.ad_set_id);
      return activeIds.length
        ? activeIds
        : adSetOptions.map((adSet) => adSet.ad_set_id);
    });
  }, [adSetOptions]);

  useEffect(() => {
    if (!selectedClientId) {
      setPreview({ zip_count: 0, sample_zip_codes: [] });
      setPreviewError('');
      return;
    }

    // Fail-safe: an empty vertical or territory selection means "push
    // nothing", never "push everything" — skip the preview entirely so the
    // count reads 0 and the push button stays disabled.
    if (!selectedVerticalIds.length || !selectedTerritoryIds.length) {
      setPreview({ zip_count: 0, sample_zip_codes: [] });
      setPreviewError('');
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');

    previewFacebookZipTargeting({
      clientId: selectedClientId,
      verticalIds: selectedVerticalIds,
      territoryIds: selectedTerritoryIds,
      includeFilteredOut,
      sampleSize: PREVIEW_SAMPLE_SIZE,
    })
      .then((response) => {
        setPreview(response.data);
      })
      .catch((error) => {
        setPreviewError(error?.message || 'Failed to preview ZIPs.');
        setPreview({ zip_count: 0, sample_zip_codes: [] });
      })
      .finally(() => {
        setPreviewLoading(false);
      });
  }, [selectedClientId, selectedVerticalIds, selectedTerritoryIds, includeFilteredOut]);

  useEffect(() => {
    if (!selectedClientId || !selectedAdAccountId || selectedAdSetIds.length !== 1) {
      setAdSetTargeting(null);
      setAdSetTargetingError('');
      return;
    }

    const adSetId = selectedAdSetIds[0];
    setAdSetTargetingLoading(true);
    setAdSetTargetingError('');
    fetchFacebookAdSetTargeting({
      clientId: selectedClientId,
      adAccountId: selectedAdAccountId,
      adSetId,
    })
      .then((response) => {
        setAdSetTargeting(response.data);
      })
      .catch((error) => {
        setAdSetTargeting(null);
        setAdSetTargetingError(error?.message || 'Failed to load ad set targeting.');
      })
      .finally(() => setAdSetTargetingLoading(false));
  }, [selectedClientId, selectedAdAccountId, selectedAdSetIds]);

  // Latest recorded state per ad set — only used to stamp "pushed X ago"
  // onto the ad set picker. The visible history comes from push events.
  const loadPushLog = () => {
    if (!selectedClientId) {
      setPushLog([]);
      return;
    }
    fetchFacebookZipPushLog({
      clientId: selectedClientId,
      limit: 100,
    })
      .then((response) => {
        setPushLog(response.data || []);
      })
      .catch(() => {
        setPushLog([]);
      });
  };

  const loadPushHistory = () => {
    if (!selectedClientId) {
      setPushHistory([]);
      return;
    }
    setPushHistoryLoading(true);
    setPushHistoryError('');
    fetchFacebookZipPushHistory({ clientId: selectedClientId, limit: 25 })
      .then((response) => {
        setPushHistory(response.data || []);
      })
      .catch((error) => {
        setPushHistory([]);
        setPushHistoryError(error?.message || 'Failed to load push history.');
      })
      .finally(() => setPushHistoryLoading(false));
  };

  useEffect(() => {
    loadPushLog();
    loadPushHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // Coverage map: centroids for the current selection.
  useEffect(() => {
    if (
      !selectedClientId ||
      !selectedVerticalIds.length ||
      !selectedTerritoryIds.length
    ) {
      setSelectionMapItems([]);
      setMapTruncated(false);
      return;
    }
    let cancelled = false;
    setMapLoading(true);
    fetchFacebookZipPreviewMap({
      clientId: selectedClientId,
      verticalIds: selectedVerticalIds,
      territoryIds: selectedTerritoryIds,
      includeFilteredOut,
      includeGeometry: true,
    })
      .then((response) => {
        if (cancelled) return;
        setSelectionMapItems(response?.data?.items || []);
        setMapTruncated(Boolean(response?.data?.truncated));
      })
      .catch(() => {
        if (cancelled) return;
        setSelectionMapItems([]);
        setMapTruncated(false);
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClientId, selectedVerticalIds, selectedTerritoryIds, includeFilteredOut]);

  // When one ad set is inspected, plot the ZIPs its live targeting would
  // lose — they sit outside the current selection, so they need their own
  // centroid lookup.
  useEffect(() => {
    const liveZips = adSetTargeting
      ? [
          ...(adSetTargeting.direct_zip_codes || []),
          ...(adSetTargeting.db_cluster_zip_codes || []),
        ]
      : [];
    if (!liveZips.length || !selectionMapItems.length) {
      setRemovedMapItems([]);
      return;
    }
    const selectionZipSet = new Set(selectionMapItems.map((item) => item.zip));
    const removedZips = [...new Set(liveZips)].filter(
      (zip) => !selectionZipSet.has(zip)
    );
    if (!removedZips.length) {
      setRemovedMapItems([]);
      return;
    }
    let cancelled = false;
    fetchFacebookZipPreviewMap({
      clientId: selectedClientId,
      zips: removedZips.slice(0, 3000),
      includeGeometry: true,
    })
      .then((response) => {
        if (cancelled) return;
        setRemovedMapItems(
          (response?.data?.items || []).map((item) => ({
            ...item,
            status: 'remove',
          }))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setRemovedMapItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [adSetTargeting, selectionMapItems, selectedClientId]);

  const mapHasDiff = Boolean(adSetTargeting) && selectedAdSetIds.length === 1;

  const mapItems = useMemo(() => {
    if (!mapHasDiff) {
      return selectionMapItems.map((item) => ({ ...item, status: 'selected' }));
    }
    const liveZipSet = new Set([
      ...(adSetTargeting.direct_zip_codes || []),
      ...(adSetTargeting.db_cluster_zip_codes || []),
    ]);
    return [
      ...selectionMapItems.map((item) => ({
        ...item,
        status: liveZipSet.has(item.zip) ? 'keep' : 'add',
      })),
      ...removedMapItems,
    ];
  }, [mapHasDiff, selectionMapItems, removedMapItems, adSetTargeting]);

  const handleConfirmedPush = async () => {
    if (
      !selectedClientId ||
      !selectedAdAccountId ||
      !selectedAdSetIds.length ||
      !selectedVerticalIds.length ||
      !selectedTerritoryIds.length
    ) {
      return;
    }

    setPushLoading(true);
    setPushError('');
    setLastPush(null);

    // One push request per group — with vertical matching on, each
    // campaign's ad sets receive their own vertical's ZIPs.
    const failures = [];
    const groupSummaries = [];
    let pushedAdSetIds = [];
    const batchStamp = Date.now();

    for (const group of pushGroups) {
      if (!group.adSets.length) {
        continue;
      }
      const groupAdSetIds = group.adSets.map((adSet) => adSet.ad_set_id);
      try {
        const response = await pushFacebookZipTargeting({
          client_id: selectedClientId,
          ad_account_id: selectedAdAccountId,
          ad_set_ids: groupAdSetIds,
          ad_set_names: group.adSets.map((adSet) => adSet.ad_set_name || ''),
          vertical_ids: group.verticalIds,
          territory_ids: selectedTerritoryIds,
          include_filtered_out: includeFilteredOut,
          source_type: 'campaign_builder_ui',
          source_id: `campaign-builder-${batchStamp}-${group.key}`,
          csv_name: `Campaign Builder ${new Date().toISOString()}`,
        });
        const results = response?.data?.results || [];
        const failed = results.filter((entry) => entry.success === false);
        const pushedCount =
          response?.data?.pushed_count ?? results.length - failed.length;
        pushedAdSetIds = [
          ...pushedAdSetIds,
          ...results
            .filter((entry) => entry.success !== false)
            .map((entry) => entry.ad_set_id),
        ];
        if (failed.length) {
          failures.push(
            `${group.label || 'batch'}: failed for ${failed
              .map((entry) => entry.ad_set_name || entry.ad_set_id)
              .join(', ')} (${failed[0].error || 'unknown error'})`
          );
        }
        if (pushedCount > 0) {
          groupSummaries.push(
            `${group.label ? `${group.label}: ` : ''}${response?.data?.zip_count ?? '?'} zips → ${pushedCount} ad set${pushedCount === 1 ? '' : 's'}`
          );
        }
      } catch (error) {
        const detail =
          error?.response?.data?.detail ||
          error?.message ||
          'Failed to push ZIP targeting.';
        failures.push(`${group.label || 'batch'}: ${detail}`);
      }
    }

    loadPushLog();
    loadPushHistory();
    setPushLoading(false);

    if (failures.length) {
      // Keep the dialog open so failures are read in context; anything that
      // already pushed stays pushed on Facebook and is listed in History.
      const detail = [
        groupSummaries.length ? `Pushed ${groupSummaries.join('; ')}.` : null,
        `Failed — ${failures.join(' | ')}`,
      ]
        .filter(Boolean)
        .join(' ');
      setPushError(detail);
      message.error(detail);
      return;
    }

    setLastPush({
      adAccountId: selectedAdAccountId,
      adSetIds: pushedAdSetIds.length ? pushedAdSetIds : [...selectedAdSetIds],
    });
    setConfirmOpen(false);
    message.success(`ZIP targeting pushed — ${groupSummaries.join('; ')}.`);
  };

  const accountOptions = structure;
  const campaignOptions = selectedAccount?.campaigns || [];

  // Count ZIPs for the currently selected vertical(s) only, so a shared
  // territory's count tracks the chosen vertical and matches the ZIP preview
  // instead of summing ZIPs across every vertical.
  const territoryZipCountFor = useCallback(
    (option) => {
      const map = includeFilteredOut
        ? option.vertical_zip_counts_all || option.vertical_zip_counts
        : option.vertical_zip_counts;
      if (!map) {
        return option.active_zip_count || 0;
      }
      const verticalKeys = selectedVerticalIds.length
        ? selectedVerticalIds
        : Object.keys(map);
      return verticalKeys.reduce(
        (sum, verticalId) => sum + (Number(map[verticalId]) || 0),
        0
      );
    },
    [selectedVerticalIds, includeFilteredOut]
  );

  const getTerritoryLabel = useCallback((option) => {
    const name = option.name || option.territory || '';
    const count = territoryZipCountFor(option);
    const summary = territoryBudgetSummary[Number(option.id)];
    if (summary) {
      if (summary.remaining > 0) {
        return `${name} (${count} zips, ${USD_CURRENCY_FORMATTER.format(summary.remaining)} left)`;
      }
      return `${name} (${count} zips, no budget left)`;
    }
    return `${name} (${count} zips)`;
  }, [territoryBudgetSummary, territoryZipCountFor]);

  // Segment rows for the budget table: one row per budget scope
  // (territory × vertical), plus rows for territories with ZIPs but no
  // budget at all — visible and flagged, never silently omitted.
  const budgetSegmentRows = useMemo(() => {
    const selectedVerticalIdSet = new Set(
      selectedVerticalIds.map((id) => Number(id))
    );
    const rows = [];
    const coveredTerritoryIds = new Set();

    budgetSegments.forEach((row, index) => {
      const totalAmount = Number(row?.budget_amount);
      if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
        return;
      }
      // Dedupe: a segment reports its vertical both as vertical_id and
      // inside vertical_ids — summing ZIP counts per key would double them.
      const rowVerticalIds = [
        ...new Set(
          [
            ...(Array.isArray(row?.vertical_ids) ? row.vertical_ids : []),
            ...(row?.vertical_id ? [row.vertical_id] : []),
          ].map(Number)
        ),
      ].filter((id) => Number.isFinite(id) && id > 0);
      const appliesToSelection =
        !rowVerticalIds.length ||
        rowVerticalIds.some((id) => selectedVerticalIdSet.has(id));
      if (!appliesToSelection) {
        return;
      }

      const territoryIdSet = new Set();
      if (row?.location_id) {
        territoryIdSet.add(Number(row.location_id));
      }
      (Array.isArray(row?.location_ids) ? row.location_ids : []).forEach(
        (locationId) => territoryIdSet.add(Number(locationId))
      );
      const segTerritoryIds = [...territoryIdSet].filter(
        (territoryId) => Number.isFinite(territoryId) && territoryId > 0
      );
      segTerritoryIds.forEach((territoryId) =>
        coveredTerritoryIds.add(territoryId)
      );

      const verticalKeys = rowVerticalIds.length
        ? rowVerticalIds.filter((id) => selectedVerticalIdSet.has(id))
        : selectedVerticalIds.map((id) => Number(id));
      const zipTerritories = segTerritoryIds.length
        ? territories.filter((territory) =>
            segTerritoryIds.includes(Number(territory.id))
          )
        : territories;
      let zipCount = 0;
      zipTerritories.forEach((territory) => {
        const countMap = includeFilteredOut
          ? territory.vertical_zip_counts_all || territory.vertical_zip_counts
          : territory.vertical_zip_counts;
        if (!countMap) {
          zipCount += territory.active_zip_count || 0;
          return;
        }
        verticalKeys.forEach((verticalId) => {
          zipCount += Number(countMap[verticalId]) || 0;
        });
      });

      rows.push({
        key: row.segment_key || `segment-${index}`,
        territoryIds: segTerritoryIds,
        territoryNames:
          Array.isArray(row.location_names) && row.location_names.length
            ? row.location_names
            : [],
        verticalNames:
          Array.isArray(row.vertical_names) && row.vertical_names.length
            ? row.vertical_names
            : [],
        isClientWide: !segTerritoryIds.length,
        zipCount,
        budget: totalAmount,
        remaining: Number(row.budget_remaining || 0),
        unbudgeted: false,
      });
    });

    rows.sort((a, b) => {
      if (a.isClientWide !== b.isClientWide) {
        return a.isClientWide ? -1 : 1;
      }
      const aExhausted = a.remaining <= 0 ? 1 : 0;
      const bExhausted = b.remaining <= 0 ? 1 : 0;
      if (aExhausted !== bExhausted) {
        return aExhausted - bExhausted;
      }
      return b.remaining - a.remaining;
    });

    const hasClientWide = rows.some((row) => row.isClientWide);
    const unbudgeted = territories
      .filter(
        (territory) =>
          !coveredTerritoryIds.has(Number(territory.id)) &&
          !hasClientWide &&
          territoryIsTargetable(territory)
      )
      .map((territory) => ({
        key: `unbudgeted-${territory.id}`,
        territoryIds: [Number(territory.id)],
        territoryNames: [
          territory.name || territory.territory || `Territory ${territory.id}`,
        ],
        verticalNames: [],
        isClientWide: false,
        zipCount: territoryZipCountFor(territory),
        budget: null,
        remaining: null,
        unbudgeted: true,
      }));

    return [...rows, ...unbudgeted];
  }, [
    budgetSegments,
    selectedVerticalIds,
    territories,
    includeFilteredOut,
    territoryZipCountFor,
  ]);

  const allTerritoryIdsWithZips = useMemo(
    () =>
      territories
        .filter(territoryIsTargetable)
        .map((territory) => Number(territory.id)),
    [territories]
  );

  const segmentRowTerritoryIds = useCallback(
    (row) => (row.isClientWide ? allTerritoryIdsWithZips : row.territoryIds),
    [allTerritoryIdsWithZips]
  );

  const toggleSegmentRow = (row) => {
    const ids = segmentRowTerritoryIds(row);
    if (!ids.length) {
      return;
    }
    setSelectedTerritoryIds((prev) => {
      const prevSet = new Set(prev.map((id) => Number(id)));
      const allSelected = ids.every((id) => prevSet.has(id));
      if (allSelected) {
        ids.forEach((id) => prevSet.delete(id));
      } else {
        ids.forEach((id) => prevSet.add(id));
      }
      return [...prevSet];
    });
  };

  const adSetLastPushById = useMemo(() => {
    const map = {};
    (pushLog || []).forEach((entry) => {
      if (entry.ad_set_id && !map[entry.ad_set_id]) {
        map[entry.ad_set_id] = entry;
      }
    });
    return map;
  }, [pushLog]);

  const timeAgo = (value) => {
    if (!value) {
      return '';
    }
    const then = new Date(value).getTime();
    if (!Number.isFinite(then)) {
      return '';
    }
    const minutes = Math.floor((Date.now() - then) / 60000);
    if (minutes < 60) {
      return `${Math.max(minutes, 1)}m ago`;
    }
    if (minutes < 60 * 24) {
      return `${Math.floor(minutes / 60)}h ago`;
    }
    return `${Math.floor(minutes / (60 * 24))}d ago`;
  };

  const adSetChipLabel = (adSet) => {
    const base = `${adSet.ad_set_name || `Ad Set ${adSet.ad_set_id}`}${
      adSet.active ? '' : ' (Inactive)'
    }`;
    const lastPush = adSetLastPushById[adSet.ad_set_id];
    if (lastPush?.updated_at) {
      return `${base} · ${timeAgo(lastPush.updated_at)} (${lastPush.zip_count})`;
    }
    return base;
  };

  // Checking a campaign targets all of its active ad sets — pushes almost
  // always go to every active ad set, so that's the default. Individual
  // chips still toggle exceptions.
  const toggleCampaign = (campaignId) => {
    const campaign = campaignOptions.find(
      (item) => item.campaign_id === campaignId
    );
    const allAdSetIds = (campaign?.ad_sets || []).map(
      (adSet) => adSet.ad_set_id
    );
    const activeAdSetIds = (campaign?.ad_sets || [])
      .filter((adSet) => adSet.active)
      .map((adSet) => adSet.ad_set_id);
    const isSelected = selectedCampaignIds.includes(campaignId);
    if (isSelected) {
      setSelectedCampaignIds((prev) => prev.filter((id) => id !== campaignId));
      setSelectedAdSetIds((prev) =>
        prev.filter((id) => !allAdSetIds.includes(id))
      );
    } else {
      setSelectedCampaignIds((prev) => [...prev, campaignId]);
      const idsToAdd = activeAdSetIds.length ? activeAdSetIds : allAdSetIds;
      setSelectedAdSetIds((prev) => [...new Set([...prev, ...idsToAdd])]);
    }
  };

  const toggleAdSet = (adSetId) => {
    setSelectedAdSetIds((prev) =>
      prev.includes(adSetId)
        ? prev.filter((id) => id !== adSetId)
        : [...prev, adSetId]
    );
  };

  // Optimistic mirror of a confirmed Facebook status change so the table
  // reflects it immediately; the next structure fetch is authoritative (the
  // backend busts its cache on every toggle).
  const applyLocalObjectStatus = (objectType, objectId, nextStatus) => {
    const isActive = nextStatus === 'ACTIVE';
    setStructure((prev) =>
      prev.map((account) => ({
        ...account,
        campaigns: (account.campaigns || []).map((campaign) => {
          if (objectType === 'campaign' && campaign.campaign_id === objectId) {
            return { ...campaign, status: nextStatus, active: isActive };
          }
          if (objectType === 'ad_set') {
            const adSets = campaign.ad_sets || [];
            if (!adSets.some((adSet) => adSet.ad_set_id === objectId)) {
              return campaign;
            }
            return {
              ...campaign,
              ad_sets: adSets.map((adSet) =>
                adSet.ad_set_id === objectId
                  ? { ...adSet, active: isActive, effective_status: nextStatus }
                  : adSet
              ),
            };
          }
          return campaign;
        }),
      }))
    );
  };

  const confirmObjectStatus = () => {
    if (!statusTarget || !selectedClientId) {
      return;
    }
    setStatusSaving(true);
    setFacebookObjectStatus({
      client_id: selectedClientId,
      object_type: statusTarget.objectType,
      object_id: statusTarget.objectId,
      object_name: statusTarget.objectName,
      status: statusTarget.nextStatus,
      account_id: selectedAdAccountId,
    })
      .then(() => {
        applyLocalObjectStatus(
          statusTarget.objectType,
          statusTarget.objectId,
          statusTarget.nextStatus
        );
        message.success(
          `${statusTarget.objectName} is now ${
            statusTarget.nextStatus === 'ACTIVE' ? 'active' : 'paused'
          } on Facebook.`
        );
        setStatusTarget(null);
      })
      .catch((error) => {
        message.error(
          error?.response?.data?.detail ||
            error?.message ||
            'Failed to update status on Facebook.'
        );
      })
      .finally(() => setStatusSaving(false));
  };

  const SOURCE_LABELS = {
    campaign_builder_ui: 'Builder',
    zip_submission: 'ZIP approval',
    dashboard_ui: 'Dashboard',
    management_command: 'Command',
  };

  const confirmCampaigns = useMemo(
    () =>
      selectedCampaigns.map((campaign) => ({
        id: campaign.campaign_id,
        name: campaign.campaign_name || `Campaign ${campaign.campaign_id}`,
        // Canonical vertical name so the mismatch check compares like with
        // like ("Roofs" alias resolves to "Roofing", matching the options).
        vertical: campaignVerticalInfo(campaign).name,
      })),
    [selectedCampaigns, campaignVerticalInfo]
  );

  const confirmAdSets = useMemo(
    () => adSetOptions.filter((adSet) => selectedAdSetIds.includes(adSet.ad_set_id)),
    [adSetOptions, selectedAdSetIds]
  );

  const confirmVerticalNames = useMemo(
    () =>
      verticals
        .filter((vertical) => selectedVerticalIds.includes(vertical.id))
        .map((vertical) => vertical.vertical)
        .filter(Boolean),
    [verticals, selectedVerticalIds]
  );

  const confirmTerritories = useMemo(
    () =>
      territories
        .filter((territory) => selectedTerritoryIds.includes(territory.id))
        .map((territory) => {
          const summary = territoryBudgetSummary[Number(territory.id)];
          return {
            id: territory.id,
            name: territory.name || territory.territory || `Territory ${territory.id}`,
            budgeted: Boolean(summary),
            exhausted: Boolean(summary) && summary.remaining <= 0,
            clientWideFunded: !summary && clientWideFunded,
          };
        }),
    [territories, selectedTerritoryIds, territoryBudgetSummary, clientWideFunded]
  );

  const campaignByAdSetId = useMemo(() => {
    const map = {};
    selectedCampaigns.forEach((campaign) => {
      (campaign.ad_sets || []).forEach((adSet) => {
        if (adSet?.ad_set_id && !map[adSet.ad_set_id]) {
          map[adSet.ad_set_id] = campaign;
        }
      });
    });
    return map;
  }, [selectedCampaigns]);

  const matchedCampaignVerticalIds = useMemo(() => {
    const ids = new Set();
    selectedCampaigns.forEach((campaign) => {
      const info = campaignVerticalInfo(campaign);
      if (info.id) {
        ids.add(info.id);
      }
    });
    return ids;
  }, [selectedCampaigns, campaignVerticalInfo]);

  // Default the mode by intent: pushing campaigns from two or more verticals
  // at once almost always means "each gets its own ZIPs" — a single shared
  // batch would send the union everywhere.
  useEffect(() => {
    setMatchVerticalsToCampaigns(matchedCampaignVerticalIds.size >= 2);
  }, [matchedCampaignVerticalIds]);

  // Push batches: with vertical matching on, ad sets are grouped by their
  // campaign's ?v= vertical and each group pushes that vertical's ZIPs. Ad
  // sets from untagged campaigns fall back to the Step 2 selection.
  const pushGroups = useMemo(() => {
    if (!matchVerticalsToCampaigns) {
      return [
        {
          key: 'all',
          verticalIds: selectedVerticalIds,
          label: null,
          unmatched: false,
          adSets: confirmAdSets,
        },
      ];
    }
    const groupsByKey = new Map();
    confirmAdSets.forEach((adSet) => {
      const campaign = campaignByAdSetId[adSet.ad_set_id];
      const info = campaign
        ? campaignVerticalInfo(campaign)
        : { id: null, name: null };
      const key = info.id ? `v${info.id}` : 'unmatched';
      const existing = groupsByKey.get(key) || {
        key,
        verticalIds: info.id ? [info.id] : selectedVerticalIds,
        label: info.id
          ? verticals.find((vertical) => vertical.id === info.id)?.vertical ||
            info.name
          : null,
        unmatched: !info.id,
        adSets: [],
      };
      existing.adSets.push(adSet);
      groupsByKey.set(key, existing);
    });
    return [...groupsByKey.values()];
  }, [
    matchVerticalsToCampaigns,
    confirmAdSets,
    campaignByAdSetId,
    campaignVerticalInfo,
    selectedVerticalIds,
    verticals,
  ]);

  const stepHeader = (step, title, subheader = null) => (
    <CardHeader
      title={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={step}
            size="small"
            sx={{
              bgcolor: 'text.primary',
              color: 'background.paper',
              fontWeight: 700,
              letterSpacing: '0.08em',
              borderRadius: 10,
            }}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
      }
      subheader={subheader}
      sx={{ py: 1.5 }}
    />
  );

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          {stepHeader('STEP 1', 'Scope — client, account, campaigns, ad sets')}
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              {clientsError && <Alert severity="error">{clientsError}</Alert>}
              <Stack
                marginTop={1}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: { xs: 'stretch', md: 'flex-start' } }}
              >
                <Box sx={{ flex: 2 }}>
                  <ClientDropdown
                    value={selectedClientId || ''}
                    onChange={(value, option) => {
                      setSelectedClientId(value || null);
                      setSelectedClientName(option?.label || '');
                    }}
                    label="Client"
                  />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="ad-account-select-label">Facebook Ad Account</InputLabel>
                    <Select
                      labelId="ad-account-select-label"
                      value={selectedAdAccountId || ''}
                      label="Facebook Ad Account"
                      onChange={(event) => setSelectedAdAccountId(event.target.value)}
                      disabled={structureLoading || !accountOptions.length}
                    >
                      {accountOptions.map((account) => (
                        <MenuItem key={account.account_id} value={account.account_id}>
                          {account.account_name || account.account_id}
                          {(account.client_count || 0) > 1
                            ? ` — shared, ${account.client_count} clients`
                            : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Box>
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">Campaigns &amp; ad sets</Typography>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    {structureRefreshing && !structureLoading && (
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <CircularProgress size={13} />
                        <Typography variant="caption" color="text.secondary">
                          Updating live statuses…
                        </Typography>
                      </Stack>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showInactiveCampaigns}
                          onChange={(event) => setShowInactiveCampaigns(event.target.checked)}
                          size="small"
                        />
                      }
                      label="Show inactive"
                    />
                  </Stack>
                </Stack>
                {structureLoading ? (
                  <CircularProgress size={20} />
                ) : campaignOptions.length ? (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Campaign</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">30-day spend</TableCell>
                          <TableCell>Ad sets</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaignOptions.map((campaign) => {
                          const checked = selectedCampaignIds.includes(campaign.campaign_id);
                          const campaignVertical = campaignVerticalInfo(campaign).name;
                          const statusLabel =
                            campaign.status || (campaign.active ? 'Active' : 'Inactive');
                          const isActive =
                            String(statusLabel).trim().toUpperCase() === 'ACTIVE';
                          const campaignAdSets = campaign.ad_sets || [];
                          return (
                            <TableRow
                              key={campaign.campaign_id}
                              hover
                              onClick={() => toggleCampaign(campaign.campaign_id)}
                              sx={{
                                cursor: 'pointer',
                                ...(isActive ? {} : { backgroundColor: 'action.hover' }),
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox size="small" checked={checked} />
                              </TableCell>
                              <TableCell
                                sx={{ color: isActive ? 'text.primary' : 'text.secondary' }}
                              >
                                {campaign.campaign_name || `Campaign ${campaign.campaign_id}`}
                              </TableCell>
                              <TableCell>
                                {campaignVertical ? (
                                  <Chip
                                    label={campaignVertical}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 20 }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Chip
                                    label={statusLabel}
                                    size="small"
                                    color={isActive ? 'success' : 'default'}
                                    variant="outlined"
                                    sx={{ height: 20 }}
                                  />
                                  {isStaffUser && (
                                    <Tooltip
                                      title={
                                        isActive
                                          ? 'Pause campaign on Facebook'
                                          : 'Resume campaign on Facebook'
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setStatusTarget({
                                            objectType: 'campaign',
                                            objectId: campaign.campaign_id,
                                            objectName:
                                              campaign.campaign_name ||
                                              `Campaign ${campaign.campaign_id}`,
                                            nextStatus: isActive ? 'PAUSED' : 'ACTIVE',
                                          });
                                        }}
                                      >
                                        {isActive ? (
                                          <PauseCircleOutlineIcon fontSize="small" />
                                        ) : (
                                          <PlayCircleOutlineIcon
                                            fontSize="small"
                                            color="success"
                                          />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                {campaign.last_30_day_spend != null
                                  ? USD_WHOLE_FORMATTER.format(campaign.last_30_day_spend)
                                  : '—'}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 380 }}>
                                {checked ? (
                                  campaignAdSets.length ? (
                                    campaignAdSets.map((adSet) => {
                                      const adSetSelected = selectedAdSetIds.includes(
                                        adSet.ad_set_id
                                      );
                                      // The trailing "delete" slot doubles as the
                                      // staff pause/resume control for the ad set.
                                      const adSetStatusProps = isStaffUser
                                        ? {
                                            onDelete: (event) => {
                                              event.stopPropagation();
                                              setStatusTarget({
                                                objectType: 'ad_set',
                                                objectId: adSet.ad_set_id,
                                                objectName:
                                                  adSet.ad_set_name ||
                                                  `Ad Set ${adSet.ad_set_id}`,
                                                nextStatus: adSet.active
                                                  ? 'PAUSED'
                                                  : 'ACTIVE',
                                              });
                                            },
                                            deleteIcon: (
                                              <Tooltip
                                                title={
                                                  adSet.active
                                                    ? 'Pause ad set on Facebook'
                                                    : 'Resume ad set on Facebook'
                                                }
                                              >
                                                {adSet.active ? (
                                                  <PauseCircleOutlineIcon />
                                                ) : (
                                                  <PlayCircleOutlineIcon />
                                                )}
                                              </Tooltip>
                                            ),
                                          }
                                        : {};
                                      return (
                                        <Chip
                                          key={adSet.ad_set_id}
                                          label={adSetChipLabel(adSet)}
                                          size="small"
                                          color={adSetSelected ? 'primary' : 'default'}
                                          variant={adSetSelected ? 'filled' : 'outlined'}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            toggleAdSet(adSet.ad_set_id);
                                          }}
                                          sx={{ mr: 0.5, mb: 0.5, height: 22 }}
                                          {...adSetStatusProps}
                                        />
                                      );
                                    })
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No ad sets
                                    </Typography>
                                  )
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    {campaignAdSets.length} ad set
                                    {campaignAdSets.length === 1 ? '' : 's'}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : selectedClientId ? (
                  <Typography color="text.secondary">
                    No campaigns found for this ad account.
                  </Typography>
                ) : (
                  <Typography color="text.secondary">
                    Select a client to load campaigns.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Click a row to include a campaign; click its ad-set chips to pick push
                  targets. Chips show the last recorded push.
                  {isStaffUser &&
                    ' Use the pause/play controls to stop or restart delivery on Facebook.'}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          {stepHeader('STEP 2', 'Select ZIPs — segments, budget, coverage')}
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: 'stretch' }}
              >
                <Autocomplete
                  multiple
                  options={verticals}
                  getOptionLabel={(option) => option.vertical || ''}
                  value={verticals.filter((vert) => selectedVerticalIds.includes(vert.id))}
                  onChange={(_, value) => setSelectedVerticalIds(value.map((item) => item.id))}
                  loading={verticalsLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Verticals"
                      placeholder="Select verticals"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  options={territories}
                  getOptionLabel={getTerritoryLabel}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const summary = territoryBudgetSummary[Number(option.id)];
                      const exhausted = Boolean(summary) && summary.remaining <= 0;
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          {...tagProps}
                          size="small"
                          label={getTerritoryLabel(option)}
                          color={exhausted ? 'error' : 'default'}
                          variant={exhausted ? 'outlined' : 'filled'}
                        />
                      );
                    })
                  }
                  renderOption={(props, option) => {
                    const summary = territoryBudgetSummary[Number(option.id)];
                    const exhausted = Boolean(summary) && summary.remaining <= 0;
                    const paused = Boolean(option.is_paused);
                    const name = option.name || option.territory || '';
                    const count = territoryZipCountFor(option);
                    const remainingText = summary
                      ? summary.remaining > 0
                        ? `${USD_CURRENCY_FORMATTER.format(summary.remaining)} left`
                        : 'no budget left'
                      : null;
                    return (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: exhausted || paused ? 'text.secondary' : 'text.primary',
                              textDecoration: exhausted ? 'line-through' : 'none',
                            }}
                          >
                            {name} ({count} zips){paused ? ' · Paused' : ''}
                          </Typography>
                          {paused && (
                            <Typography variant="caption" sx={{ color: 'warning.main' }}>
                              Paused out of Facebook targeting — pushes zero ZIPs
                            </Typography>
                          )}
                          {remainingText && (
                            <Typography
                              variant="caption"
                              sx={{ color: exhausted ? 'error.main' : 'success.main' }}
                            >
                              {remainingText}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  value={territories.filter((territory) => selectedTerritoryIds.includes(territory.id))}
                  onChange={(_, value) => setSelectedTerritoryIds(value.map((item) => item.id))}
                  loading={territoriesLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Territories"
                      placeholder="Select territories"
                      fullWidth
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ marginTop: 1 }}>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => applyFundedTerritories()}
                  disabled={!territories.length || budgetTerritoriesLoading}
                >
                  Funded Territories (This Month)
                </Button>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedTerritoryIds(
                    territories
                      .filter((t) => {
                        if (!territoryIsTargetable(t)) return false;
                        const summary = territoryBudgetSummary[Number(t.id)];
                        return !summary || summary.remaining > 0;
                      })
                      .map((t) => t.id)
                  )}
                  disabled={!territories.some(territoryIsTargetable)}
                >
                  All Territories (with budget)
                </Button>
                <Button
                  color="primary"
                  size="small"
                  variant="text"
                  onClick={() => setSelectedTerritoryIds([])}
                  disabled={!selectedTerritoryIds.length}
                >
                  Clear selection
                </Button>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  Segment budgets ({budgetMonthLabel || 'Current month'})
                </Typography>
                {budgetTerritoriesLoading ? (
                  <CircularProgress size={18} />
                ) : budgetSegmentRows.length ? (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Territory</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell align="right">ZIPs</TableCell>
                          <TableCell align="right">Budget</TableCell>
                          <TableCell align="right">Remaining</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {budgetSegmentRows.map((row) => {
                          const rowIds = segmentRowTerritoryIds(row);
                          const selectedSet = new Set(
                            selectedTerritoryIds.map((id) => Number(id))
                          );
                          const selectedCount = rowIds.filter((id) =>
                            selectedSet.has(id)
                          ).length;
                          const allSelected =
                            rowIds.length > 0 && selectedCount === rowIds.length;
                          const exhausted = !row.unbudgeted && row.remaining <= 0;
                          return (
                            <TableRow
                              key={row.key}
                              hover
                              onClick={() => toggleSegmentRow(row)}
                              sx={{
                                cursor: 'pointer',
                                ...(exhausted
                                  ? { backgroundColor: 'action.hover' }
                                  : {}),
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={allSelected}
                                  indeterminate={selectedCount > 0 && !allSelected}
                                />
                              </TableCell>
                              <TableCell
                                sx={{ color: exhausted ? 'text.disabled' : 'text.primary' }}
                              >
                                {row.isClientWide
                                  ? 'All territories'
                                  : row.territoryNames.join(', ') || '—'}
                                {row.isClientWide && (
                                  <Chip
                                    label="Client-wide"
                                    size="small"
                                    sx={{ ml: 1, height: 18 }}
                                  />
                                )}
                                {exhausted && (
                                  <Chip
                                    label="No budget left"
                                    size="small"
                                    color="error"
                                    sx={{ ml: 1, height: 18 }}
                                  />
                                )}
                                {row.unbudgeted && (
                                  <Chip
                                    label="No budget"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ml: 1, height: 18 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {row.verticalNames.length ? (
                                  row.verticalNames.map((name) => (
                                    <Chip
                                      key={name}
                                      label={name}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      sx={{ mr: 0.5, height: 20 }}
                                    />
                                  ))
                                ) : row.unbudgeted ? (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                ) : (
                                  <Chip label="Any vertical" size="small" sx={{ height: 20 }} />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {row.zipCount.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">
                                {row.budget != null
                                  ? USD_CURRENCY_FORMATTER.format(row.budget)
                                  : '—'}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color:
                                    row.remaining == null
                                      ? 'text.secondary'
                                      : exhausted
                                      ? 'error.main'
                                      : 'success.main',
                                }}
                              >
                                {row.remaining != null
                                  ? USD_CURRENCY_FORMATTER.format(row.remaining)
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">
                    No budget segments found for this month.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Click a row to select or deselect its territories for the push.
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeFilteredOut}
                    onChange={(event) => setIncludeFilteredOut(event.target.checked)}
                  />
                }
                label="Include filtered-out ZIPs"
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <SelectionCoverageMap
                    items={mapItems}
                    loading={mapLoading}
                    truncated={mapTruncated}
                    hasDiff={mapHasDiff}
                  />
                </Box>
                <Stack spacing={1} sx={{ width: { md: 280 } }}>
                  <Typography variant="subtitle2">ZIP preview</Typography>
                  {previewLoading ? (
                    <CircularProgress size={20} />
                  ) : preview.zip_count ? (
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {preview.zip_count.toLocaleString()}{' '}
                      <Typography component="span" variant="body2" color="text.secondary">
                        ZIPs selected
                      </Typography>
                    </Typography>
                  ) : selectedClientId &&
                    (!selectedVerticalIds.length || !selectedTerritoryIds.length) ? (
                    <Typography color="text.secondary">
                      0 ZIPs — select at least one vertical and one territory.
                    </Typography>
                  ) : (
                    <Typography color="text.secondary">No ZIPs selected yet.</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {selectedTerritoryIds.length} territor
                    {selectedTerritoryIds.length === 1 ? 'y' : 'ies'} ·{' '}
                    {selectedVerticalIds.length} vertical
                    {selectedVerticalIds.length === 1 ? '' : 's'} selected
                  </Typography>
                  {!mapHasDiff && (
                    <Typography variant="caption" color="text.secondary">
                      Select exactly one ad set in Step 1 to color the map as a
                      diff against its live Facebook targeting.
                    </Typography>
                  )}
                </Stack>
              </Stack>
              {previewError && <Alert severity="error">{previewError}</Alert>}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          {stepHeader('STEP 3', 'Review & push')}
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ md: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack>
                  <Typography variant="body1" fontWeight={600}>
                    {matchVerticalsToCampaigns && pushGroups.length > 1
                      ? `Vertical-matched push → ${pushGroups
                          .map(
                            (group) =>
                              `${group.label || 'Step 2 verticals'} (${group.adSets.length} ad set${group.adSets.length === 1 ? '' : 's'})`
                          )
                          .join(', ')} on ${selectedAccount?.account_name || selectedAdAccountId || '—'}`
                      : `Push ${preview.zip_count ? preview.zip_count.toLocaleString() : 0} ZIPs → ${confirmAdSets.length} ad set${confirmAdSets.length === 1 ? '' : 's'} on ${selectedAccount?.account_name || selectedAdAccountId || '—'}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Every push opens a review dialog with a live→new diff per ad set — no
                    direct fire.
                  </Typography>
                  {matchedCampaignVerticalIds.size >= 1 && (
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={
                        <Switch
                          size="small"
                          checked={matchVerticalsToCampaigns}
                          onChange={(event) =>
                            setMatchVerticalsToCampaigns(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Match ZIPs to each campaign&apos;s vertical (Roofs ZIPs → Roofs
                          campaign, Siding → Siding)
                        </Typography>
                      }
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setPushError('');
                      setConfirmOpen(true);
                    }}
                    disabled={
                      !selectedClientId ||
                      !selectedAdAccountId ||
                      !selectedAdSetIds.length ||
                      !selectedVerticalIds.length ||
                      !selectedTerritoryIds.length ||
                      pushLoading ||
                      previewLoading ||
                      !preview.zip_count
                    }
                  >
                    Review &amp; push…
                  </Button>
                  {lastPush && (
                    <Button
                      variant="outlined"
                      color="success"
                      component="a"
                      href={buildFacebookAdsManagerUrl(lastPush.adAccountId, lastPush.adSetIds)}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<OpenInNewIcon />}
                    >
                      View in Facebook
                    </Button>
                  )}
                </Stack>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Current selected Ad Set targeting</Typography>
                {selectedAdSetIds.length !== 1 ? (
                  <Typography color="text.secondary">
                    Select exactly one ad set to inspect its current Facebook targeting.
                  </Typography>
                ) : adSetTargetingLoading ? (
                  <CircularProgress size={20} />
                ) : adSetTargeting ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Direct zips: {adSetTargeting.direct_zip_count} | Cluster zips (from DB):{' '}
                      {adSetTargeting.db_cluster_zip_count}
                    </Typography>
                    {adSetTargeting.facebook_location_cluster_ids?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Facebook Cluster IDs: {adSetTargeting.facebook_location_cluster_ids.join(', ')}
                      </Typography>
                    ) : null}
                    {adSetTargeting.db_location_cluster_ids?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        DB Cluster IDs: {adSetTargeting.db_location_cluster_ids.join(', ')}
                      </Typography>
                    ) : null}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {(adSetTargeting.direct_zip_codes || []).slice(0, 20).map((zip) => (
                        <Chip key={`direct-${zip}`} label={zip} size="small" />
                      ))}
                      {(adSetTargeting.db_cluster_zip_codes || []).slice(0, 20).map((zip) => (
                        <Chip key={`cluster-${zip}`} label={zip} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography color="text.secondary">No targeting data found for this ad set.</Typography>
                )}
              </Stack>
              {adSetTargetingError && <Alert severity="error">{adSetTargetingError}</Alert>}
              {pushError && <Alert severity="error">{pushError}</Alert>}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          {stepHeader('HISTORY', 'Push history — newest first')}
          <Divider />
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Every ZIP push recorded for this client, from any surface — builder,
                  ZIP-submission approvals, or commands.
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => loadPushHistory()}
                  disabled={pushHistoryLoading || !selectedClientId}
                >
                  Refresh
                </Button>
              </Stack>
              {pushHistoryLoading ? (
                <CircularProgress size={20} />
              ) : pushHistoryError ? (
                <Alert severity="error">{pushHistoryError}</Alert>
              ) : pushHistory.length ? (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Vertical → Territories</TableCell>
                        <TableCell>Campaign / ad set</TableCell>
                        <TableCell align="right">ZIPs</TableCell>
                        <TableCell>By</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pushHistory.map((event) => (
                        <TableRow
                          key={event.id}
                          sx={
                            event.success
                              ? undefined
                              : { backgroundColor: 'rgba(211, 47, 47, 0.06)' }
                          }
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {event.created_at
                              ? new Date(event.created_at).toLocaleString()
                              : '—'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {event.client_name || event.client_id}
                          </TableCell>
                          <TableCell>
                            {(event.vertical_names || []).map((name) => (
                              <Chip
                                key={name}
                                label={name}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ mr: 0.5, height: 20 }}
                              />
                            ))}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {(event.territory_names || []).join(', ') || 'All territories'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {[event.campaign_name, event.ad_set_name || event.ad_set_id]
                              .filter(Boolean)
                              .join(' / ')}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {event.success ? (
                              <>
                                {event.zip_count.toLocaleString()}{' '}
                                <Typography component="span" variant="body2" sx={{ color: 'success.main' }}>
                                  +{event.added_count}
                                </Typography>{' '}
                                <Typography component="span" variant="body2" sx={{ color: 'error.main' }}>
                                  −{event.removed_count}
                                </Typography>
                              </>
                            ) : (
                              <Chip
                                label="Failed"
                                size="small"
                                color="error"
                                title={event.error}
                                sx={{ height: 20 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{event.actor_name || '—'}</TableCell>
                          <TableCell>
                            {SOURCE_LABELS[event.source_type] || event.source_type || '—'}
                          </TableCell>
                          <TableCell padding="none">
                            {event.ad_account_id && event.ad_set_id && (
                              <Button
                                size="small"
                                variant="text"
                                component="a"
                                href={buildFacebookAdsManagerUrl(event.ad_account_id, [event.ad_set_id])}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                sx={{ minWidth: 'auto', textTransform: 'none' }}
                              >
                                View
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">
                  No pushes recorded yet — history starts with the first push after this
                  update.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <PushConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmedPush}
        pushing={pushLoading}
        pushError={pushError}
        clientName={selectedClientName}
        clientId={selectedClientId}
        adAccount={selectedAccount}
        campaigns={confirmCampaigns}
        groups={pushGroups}
        matchVerticals={matchVerticalsToCampaigns}
        verticalNames={confirmVerticalNames}
        territories={confirmTerritories}
        territoryIds={selectedTerritoryIds}
        includeFilteredOut={includeFilteredOut}
      />
      <Dialog
        open={Boolean(statusTarget)}
        onClose={() => {
          if (!statusSaving) {
            setStatusTarget(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {statusTarget?.nextStatus === 'ACTIVE' ? 'Resume' : 'Pause'}{' '}
          {statusTarget?.objectType === 'ad_set' ? 'ad set' : 'campaign'} on Facebook?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusTarget?.nextStatus === 'ACTIVE'
              ? `"${statusTarget?.objectName}" will start delivering (and spending) again on ${
                  selectedClientName || 'this client'
                }'s ad account.`
              : `"${statusTarget?.objectName}" will stop delivering immediately for ${
                  selectedClientName || 'this client'
                }. You can resume it here at any time.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)} disabled={statusSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={statusTarget?.nextStatus === 'ACTIVE' ? 'success' : 'warning'}
            onClick={confirmObjectStatus}
            disabled={statusSaving}
            startIcon={
              statusSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : statusTarget?.nextStatus === 'ACTIVE' ? (
                <PlayCircleOutlineIcon />
              ) : (
                <PauseCircleOutlineIcon />
              )
            }
          >
            {statusTarget?.nextStatus === 'ACTIVE' ? 'Resume' : 'Pause'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CampaignBuilderPage;
